/*
 * Copyright (c) 2012 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.axis

import com.macrofocus.common.collection.TreeSet
import com.macrofocus.common.collection.UniversalComparator
import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.interval.SimpleBoundedInterval
import com.macrofocus.common.selection.Selection
import com.macrofocus.common.selection.SingleSelection
import org.molap.dataframe.DataFrame
import org.molap.series.Series
import org.molap.subset.SubsetDataFrame
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min

class CategoricalAxisModel<Row, C>(
    dataFrame: DataFrame<Row, C, *>,
    annotationDataFrame: DataFrame<*, C, *>?,
    subsetDataFrame: SubsetDataFrame<Row, C, *>?,
    column: C,
    probing: SingleSelection<Row>,
    selection: Selection<Row>,
    mutableFilter: MutableFilter<Row>
) : DefaultAxisModel<Row, C>(dataFrame, annotationDataFrame, subsetDataFrame, column, probing, selection, mutableFilter) {
    private var list: List<Any>? = null
    private var index: MutableMap<Any, Int>? = null
    protected override fun createBoundedInterval(): MutableBoundedInterval {
        this.min = getIndex()!!.size - 1.0
        this.max = 0.0
        val interval: MutableBoundedInterval = SimpleBoundedInterval(min, max - min, min, max)
        interval.minimumExtent = -(min - max)
        return interval
    }

    override fun createScaledBoundedInterval(): MutableBoundedInterval {
        return interval
    }

    override val isNumerical: Boolean
        get() = false

    override fun getValue(row: Row): Number? {
        return getValue(AxisModel.Companion.DATA, row)
    }

    override fun getValue(layer: Int, row: Row): Number? {
        val index = getIndex()
        val series: Series<Row,out Any?>? = this.series.get(layer)
        return if (series != null) {
            val v: Any? = series.get(row)
            index!![v]
        } else {
            null
        }
    }

    override val isDegenerate: Boolean
        get() = getIndex()!!.size == 0
    override val formattedMinimum: String
        get() {
            val l = getList()
            val value = if (l!!.size > 0) l[max(0, minimum.toInt())] else null
            return value.toString()
        }
    override val formattedMaximum: String
        get() {
            val l = getList()
            val value = if (l!!.size > 0) l[min(getIndex()!!.size - 1, maximum.toInt())] else null
            return value.toString()
        }

    override fun getFormattedValue(row: Row): String {
        return getFormattedValue(com.macrofocus.high_d.axis.AxisModel.Companion.DATA, row)
    }

    override fun getFormattedValue(layer: Int, row: Row): String {
        val value: Any? = series.get(layer)!!.get(row)
        return if (value != null) value.toString() else ""
    }

    override fun getFormattedFloorValue(value: Double): String {
        val o = getOriginalFloorValue(value)
        return if (o != null) o.toString() else ""
    }

    override fun getFormattedCeilValue(value: Double): String {
        val o = getOriginalCeilValue(value)
        return if (o != null) o.toString() else ""
    }

    override fun getFormattedValue(value: Double): String {
        val max: Double
        val min: Double
        if (!scaledInterval.isInvertedScale) {
            max = max(value, minimum)
            min = min(max, maximum)
        } else {
            max = max(value, maximum)
            min = min(max, minimum)
        }
        val v = getList()!![min.toInt()]
        return if (v != null) v.toString() else ""
    }

    override fun getOriginalFloorValue(value: Double): Any {
        val max: Double
        val min: Double
        if (!scaledInterval.isInvertedScale) {
            max = max(value, minimum)
            min = min(max, maximum)
        } else {
            max = max(value, maximum)
            min = min(max, minimum)
        }
        return getList()!![floor(min).toInt()]
    }

    override fun getOriginalCeilValue(value: Double): Any {
        val max: Double
        val min: Double
        if (!scaledInterval.isInvertedScale) {
            max = max(value, minimum)
            min = min(max, maximum)
        } else {
            max = max(value, maximum)
            min = min(max, minimum)
        }
        return getList()!![ceil(min).toInt()]
    }

    private fun getList(): List<Any>? {
        if (index == null) {
            createIndex()
        }
        return list
    }

    private fun getIndex(): Map<Any, Int>? {
        if (index == null) {
            createIndex()
        }
        return index
    }

    private fun createIndex() {
        val set: MutableSet<Any> = TreeSet<Any>(UniversalComparator() as Comparator<Any>)
        for (r in dataFrame.rows()) {
            val series: Series<Row,out Any?>? = this.series.get(AxisModel.Companion.DATA)
            if (series != null) {
                val value: Any? = series.get(r)
                if (value != null && value is Comparable<*>) {
                    set.add(value)
                }
            }
        }
        list = ArrayList<Any>(set)
        index = HashMap<Any, Int>()
        var i = 0
        for (o in list!!) {
            index!![o] = i
            i++
        }
    }
}